import { Button } from '#app/components/Buttons/Button.js';
import { ErrorBoundary } from '#app/components/ErrorBoundary/ErrorBoundary.js';
import { Link } from '#app/components/Link/index.js';
import { Text } from '#app/components/Primitives/Text.js';
import { useIsMobile } from '#app/hooks/useIsMobile.js';
import {
  RecommendationsContainerForProjects,
  RecommendationsContainerForServices,
} from './Containers.js';
import { type Page } from './types.js';
import { useRequestRecommendation } from './useRequestRecommendation.js';
import { getRoute } from '@contra/routes';
import { type ReactNode } from 'react';
import { css, styled } from 'styled-components';

const Container = styled.div<{ page: Page }>`
  ${({ page, theme }) => css`
    display: flex;
    flex-direction: column;
    align-items: center;
    background-color: ${page === 'service'
      ? theme.colorsV2.gray05
      : page === 'project'
        ? theme.colorsV2.gray20
        : theme.colorsV2.white};
    padding: 16px 32px;
    gap: 16px;
    width: 100%;

    ${theme.mediaQueries.md} {
      border-radius: 10px;
      flex-direction: row;
      justify-content: space-between;
      align-items: center;
    }
  `}
`;

const TextContainer = styled(Text)``;

const ButtonContainer = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: row;
    gap: 16px;

    & button span {
      font-weight: ${theme.fontWeights.semiBold};
      white-space: nowrap;
    }

    ${theme.mediaQueries.sm} {
      gap: 12px;
    }

    ${theme.mediaQueries.md} {
      justify-content: flex-end;
    }
  `}
`;

const CloseButton = styled(Button)`
  padding: 0;
`;

type Props = {
  readonly children: ReactNode;
  readonly isEligibleForRequestRecommendations: boolean;
  readonly page: Page;
};

/**
 * @package
 */
export const RequestRecommendationCTA = ({
  children,
  isEligibleForRequestRecommendations,
  page,
}: Props) => {
  // all pages have different layouts and styles so we need to handle them separately
  const PageContainer =
    page === 'project'
      ? RecommendationsContainerForProjects
      : RecommendationsContainerForServices;

  const isMobile = useIsMobile();

  const { handleCTAClick, handleCTADismiss, hasDismissedCTA } =
    useRequestRecommendation(page);

  if (hasDismissedCTA || !isEligibleForRequestRecommendations) {
    return null;
  }

  return (
    <ErrorBoundary
      boundaryIdentifier="request-recommendation-cta"
      renderError={() => null}
    >
      <PageContainer>
        <Container page={page}>
          <TextContainer textStyle={isMobile ? 'bodySmall' : 'bodyRegular'}>
            {children}
          </TextContainer>

          <ButtonContainer>
            <CloseButton
              onClick={handleCTADismiss}
              removeMinWidth
              size="small"
              variant="V2Link"
            >
              Close
            </CloseButton>

            <Button
              as={Link}
              onClick={handleCTAClick}
              size="small"
              to={getRoute('/independent/request-recommendation')}
            >
              Request Recommendation
            </Button>
          </ButtonContainer>
        </Container>
      </PageContainer>
    </ErrorBoundary>
  );
};
